<template>

	<v-container fluid class="pa-0">

        <!-- Toast notification component -->
		<v-snackbar
			v-model="showSnackbar"
			:color="snackbarColor"
			:timeout="3000"
			location="top"
			rounded="pill"
			elevation="4"
			class="pa-3"
		>
			<div class="d-flex align-center">
				<v-icon class="me-3" :icon="snackbarIcon"></v-icon>
				<span class="text-subtitle-1 font-weight-medium">{{ snackbarText }}</span>
			</div>
			<template v-slot:actions>
			<v-btn
				variant="text"
				icon="mdi-close"
				@click="showSnackbar = false"
			></v-btn>
			</template>
		</v-snackbar>

        <!-- Delete confirmation dialog -->
        <v-dialog v-model="showDeleteConfirm" max-width="400">
          <v-card class="rounded-xl elevation-5">
            <v-card-title class="text-h5 bg-error text-white rounded-t-xl py-4">
              <v-icon icon="mdi-delete-alert" class="me-2"></v-icon>
              {{ t('dashboard.deleteInterview') }}
            </v-card-title>
            <v-card-text class="pa-6 text-body-1">
              {{ t('dashboard.areYouSureYouWantToDeleteThisInterviewResultThisActionCannotBeUndone') }}
            </v-card-text>
            <v-card-actions class="pa-4 pb-6">
              <v-spacer></v-spacer>
              <v-btn color="grey-darken-1" variant="text" @click="showDeleteConfirm = false" rounded="pill" class="px-5">
                {{ t('generic.cancel') }}
              </v-btn>
              <v-btn
                  color="error"
                  variant="elevated"
                  @click="confirmDeleteRun"
                  rounded="pill"
                  class="px-5"
                  :loading="deletingRun"
              >
                <v-icon start icon="mdi-delete" class="me-1"></v-icon>
                {{ t('generic.delete') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Header section with gradient background -->
		<v-sheet
			color="primary"
			class="py-8 mb-8"
			rounded="0"
			elevation="1"
		>
			<v-row justify="center" class="text-center mt-2">
				<v-icon icon="mdi-view-dashboard" size="56" color="white" class="mb-4"></v-icon>
				<h1 class="text-h3 font-weight-light text-white">{{ t('dashboard.dashboard') }}</h1>
			</v-row>
			<v-row justify="center" class="text-center">
				<div class="text-subtitle-1 text-white mt-2">{{ t('dashboard.viewAndAnalyzeInterviewsChartsAndInsights') }}</div>
			</v-row>

		</v-sheet>

    <v-row class="mt-6" justify="center">
      <v-col cols="12" :md="selectedRunData ? 4 : 6">
        <v-card rounded="xl" elevation="2" class="border ml-4">
          <v-card-title class="d-flex align-center px-6 pt-5 pb-2">
            <v-icon icon="mdi-format-list-bulleted" color="primary" class="me-3"></v-icon>
            <span class="text-h6">{{ t('dashboard.interviewResults') }}</span>
          </v-card-title>

          <v-card-text class="px-6 pb-2">
            <v-select
                class="domain-select mb-2 mt-2"
                :label="$t('generic.domain')"
                :items="domainOptions"
                item-title="title"
                item-value="value"
                v-model="selectedDomain"
                density="default"
                variant="outlined"
                color="primary"
                prepend-inner-icon="mdi-domain"
                hide-details
            />

            <v-progress-circular
                v-if="isCampaignsLoading"
                indeterminate
                color="primary"
                class="mx-auto d-block my-4"
            ></v-progress-circular>

            <!-- Scoped to just this autocomplete (not global Vuetify config)
                 so the built-in empty-state text ("No data available" etc.)
                 follows the app locale without affecting any other page.
                 Vuetify's own en/ja message bundles are passed explicitly
                 since the root Vuetify config only registers "en". -->
            <v-locale-provider :locale="$i18n.locale" :messages="{ en: vuetifyEn, ja: vuetifyJa }">
            <v-autocomplete
                v-show="!isCampaignsLoading"
                v-model="selectedCampaign"
                :items="dropdownCampaigns"
                :label="$t('dashboard.filterByTopic')"
                variant="outlined"
                density="comfortable"
                hide-details
                clearable
                class="rounded-lg mt-2"
            >
              <template v-slot:prepend-item>
                <v-list-item prepend-icon="mdi-filter-variant" :title="$t('dashboard.topics')" class="text-primary"></v-list-item>
                <v-divider></v-divider>
              </template>
              <template v-slot:prepend-inner>
                <v-icon icon="mdi-magnify" color="primary"></v-icon>
              </template>
            </v-autocomplete>
            </v-locale-provider>
          </v-card-text>

          <v-card-subtitle class="px-6 pt-4 pb-0 d-flex align-center">
            <span class="font-weight-medium">{{ t('dashboard.availableRuns') }}</span>
            <v-chip
                v-if="filteredRuns.length > 0"
                color="primary"
                size="small"
                class="ml-2 px-2"
                variant="tonal"
            >
              {{ filteredRuns.length }}
            </v-chip>
          </v-card-subtitle>

          <v-divider class="mx-6 my-3"></v-divider>

          <v-card-text class="px-4 py-0 mb-4">
            <div v-if="isLoadingInterviews" class="d-flex justify-center my-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <v-list v-else class="py-0 bg-transparent" style="max-height: 400px; overflow-y: auto;">
              <v-list-item
                  v-for="run in filteredRuns"
                  :key="run.run_id"
                  @click="selectRun(run)"
                  rounded="lg"
                  :class="{'v-list-item--active': selectedRunId === run.run_id}"
                  :color="selectedRunId === run.run_id ? 'primary' : ''"
                  class="mb-2"
              >
                <template v-slot:prepend>
                  <v-avatar size="36" color="primary" variant="tonal" class="mr-3">
                    <v-icon icon="mdi-account-group" size="20"></v-icon>
                  </v-avatar>
                </template>
                <v-list-item-title class="font-weight-medium text-body-1">{{ run.campaign_name }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption mt-1">
                  {{ run.personas_count }} persona{{ run.personas_count !== 1 ? 's' : '' }}: {{ (run.persona_names || []).join(', ') }}
                </v-list-item-subtitle>
                <v-list-item-subtitle class="text-caption mt-1 d-flex align-center">
                  <v-icon icon="mdi-clock-outline" size="14" class="mr-1"></v-icon>
                  {{ formatTimestamp(run.created_at) }}
                </v-list-item-subtitle>
                <template v-slot:append>
                  <v-btn
                      icon="mdi-delete-outline"
                      color="error"
                      size="small"
                      variant="text"
                      @click.stop="promptDeleteRun(run)"
                  ></v-btn>
                </template>
              </v-list-item>

              <v-list-item
                  v-if="filteredRuns.length === 0 && !isLoadingInterviews"
                  class="my-4 text-center"
                  disabled
              >
                <v-list-item-title class="text-medium-emphasis">
                  <v-icon icon="mdi-information-outline" class="mb-1 mr-1"></v-icon>
                  {{ t('dashboard.noRunsAvailable') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Selected run details panel -->
      <v-col cols="12" md="8" v-if="selectedRunData || isLoadingRunDetails">
        <v-card
            v-if="isLoadingRunDetails"
            elevation="2"
            rounded="xl"
            class="border mr-4 pa-6"
            height="200"
        >
          <div class="d-flex flex-column justify-center align-center h-100">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            <p class="mt-4 text-medium-emphasis">{{ t('dashboard.loadingRunDetails') }}</p>
          </div>
        </v-card>

        <v-card v-else-if="selectedRunData" elevation="2" rounded="xl" class="border mr-4 pa-4">
          <v-card-title class="text-h5 text-center font-weight-bold">{{ selectedRunData.campaign_name }}</v-card-title>
          <v-divider class="mb-2"></v-divider>
          <v-card-text>
            <p><strong>{{ t('dashboard.personas') }}</strong> {{ (selectedRunData.persona_names || []).join(', ') }}</p>
            <p><strong>{{ t('dashboard.topic') }}</strong> {{ selectedRunData.query }}</p>
            <p class="text-caption text-right mt-2">
              <v-icon icon="mdi-clock-outline" size="14" class="mr-1"></v-icon>
              {{ formatTimestamp(selectedRunData.created_at) }}
            </p>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn
                @click="generateDashboard"
                color="primary"
                :loading="isLoading"
                size="large"
                rounded="pill"
                elevation="2"
                v-tooltip.bottom="$t('dashboard.generateDashboardChartsFromThisRun')"
                min-width="200"
            >
                <v-icon left class="mr-2">mdi-chart-bar</v-icon>
                {{ t('dashboard.generateDashboard') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
        <RadarChart v-if="selectedDomain === 'automotive' && bar_chart_visible" :model="radar_chart_model"></RadarChart>

        <!-- Personas List Card -->
        <!-- <v-row justify="center" v-if="personasList.length > 0">
            <v-col align="center" cols="8">
                <v-card class="pa-4">
                    <h3>Personas List</h3>
                    <v-list>
                        <v-list-item v-for="(persona, index) in personasList" :key="index">
                            <v-list-item-content>{{ persona }}</v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>
        </v-row> -->

        <v-row v-if="bar_chart_visible">
            <v-col cols="6">
                <v-card v-if="bar_chart_visible" class="h-100 ml-4">
                    <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">
                        <v-spacer></v-spacer>
                    <span>{{ t('dashboard.genderDistribution') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="$t('dashboard.displaysTheDistributionOfParticipantsByGender')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                    <v-divider class="mb-2 mx-4"></v-divider>
                    <v-card-text>
                    <VuePlotly :data="genderData" :layout="genderChartLayout" :config="chartConfig" />
                    </v-card-text>
                </v-card>

            </v-col>
            <v-col cols="6">
                <v-card v-if="bar_chart_visible" class="h-100 mr-4">
                    <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">
                        <v-spacer></v-spacer>
                        <span>{{ t('dashboard.likelihoodToAdhereToTopic') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="t('generic.info')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                    <v-divider class="mb-2 mx-4"></v-divider>
                    <v-card-text>
                        <VuePlotly :data="likelihoodChartData" :layout="likelihoodChartLayout" :config="chartConfig" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="bar_chart_visible">
            <v-col cols="6">
                <v-card v-if="bar_chart_visible" class="h-100 ml-4">
                    <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">
                        <v-spacer></v-spacer>
                        <span>{{ t('dashboard.ageDistribution') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="t('generic.info')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                    <v-divider class="mb-2 mx-4"></v-divider>
                    <v-card-text>
                        <VuePlotly :data="ageChartData" :layout="ageChartLayout" :config="chartConfig" />
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="6">
                <v-card v-if="bar_chart_visible" class="h-100 mr-4">
                    <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">                        
                        <v-spacer></v-spacer>
                        <span>{{ t('dashboard.incomeDistribution') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="t('generic.info')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                    <v-divider class="mb-2 mx-4"></v-divider>
                    <v-card-text>
                        <VuePlotly :data="incomeChartData" :layout="incomeChartLayout" :config="chartConfig" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <div>
            <v-card v-if="bar_chart_visible" class="mt-4 mx-4">
                <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">                        
                    <v-spacer></v-spacer>
                        <span>{{ t('dashboard.jobDistribution') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="t('generic.info')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                <v-divider class="mb-2 mx-4"></v-divider>
                <v-card-text>
                    <VuePlotly :data="employmentChartData" :layout="employmentChartLayout" :config="chartConfig" />
                </v-card-text>
            </v-card>
        </div>

        <div>
            <v-card v-if="bar_chart_visible" class="mt-4 mx-4">
                <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">                        
                    <v-spacer></v-spacer>
                        <span>{{ t('dashboard.keyPointsEvaluation') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="t('generic.info')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                <v-divider class="mb-2 mx-4"></v-divider>
                <v-card-text>
                    <VuePlotly :data="barChartData" :layout="barChartLayout" :config="chartConfig" />
                </v-card-text>
            </v-card>
        </div>

        <div>
            <v-card v-if="bar_chart_visible" class="mt-4 mx-4">
                <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">                        
                    <v-spacer></v-spacer>
                        <span>{{ t('dashboard.sentimentAnalysis') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="t('generic.info')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                <v-divider class="mb-2 mx-4"></v-divider>
                <v-card-text>
                    <VuePlotly :data="sentimentChartData" :layout="sentimentChartLayout" :config="chartConfig" />
                </v-card-text>
            </v-card>
        </div>

        <div>
            <v-card v-if="bar_chart_visible" class="mt-4 mx-4">
                <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">                        
                    <v-spacer></v-spacer>
                        <span>{{ t('dashboard.aspectBasedSentimentAnalysisABSA') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="t('generic.info')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                <v-divider class="mb-2 mx-4"></v-divider>
                <v-card-text>
                    <VuePlotly :data="absaChartData" :layout="absaChartLayout" :config="chartConfig" />
                </v-card-text>
            </v-card>
        </div>

        <!-- Title for the Word Cloud, placed outside the container, inside it was bugged -->
        <!-- <v-row class="mt-6" justify="center" v-if="bar_chart_visible">
            <v-col cols="auto">
                <v-title class="text-h5 font-weight-semibold text-center">Most Common Words</v-title>
            </v-col>
        </v-row> -->

        <!-- <v-container class="d-flex justify-center" v-if="bar_chart_visible">            
                <v-img :src="wordCloudUrl" alt="Word Cloud" contain max-width="800"></v-img>
        </v-container> -->


        <!-- Word Cloud Section align-items-center -->
        <!-- <v-layout wrap class="justify-center">
            <v-card class="mb-8 pa-4 mt-4 mx-4" v-if="bar_chart_visible">
                <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2"> 
                    <v-spacer></v-spacer>        
                    <span>Most Common Words</span>
                    <v-spacer></v-spacer>
                    <span
                        icon="mdi-information"
                        v-tooltip.bottom="'Info'"
                        class="ms-2"
                    >
                        <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                    </span>
                </v-card-title>
                <v-divider class="mb-4 mx-4"></v-divider>
                <div class="d-flex justify-center align-center">
                <v-img :src="wordCloudUrl" alt="Word Cloud" contain width="800" class="elevation-1 rounded-lg ma-4"></v-img>
                </div>
            </v-card>
        </v-layout> -->

        <v-container class="d-flex justify-center align-center mx-4" v-if="bar_chart_visible" style="min-height: 300px;">
            <v-card max-width="1200" style="overflow: hidden; width: 100%;">
                <v-card-title class="text-h5 font-weight-semibold d-flex align-center justify-center my-2">                        
                    <v-spacer></v-spacer>
                        <span>{{ t('dashboard.improvementsOverview') }}</span>
                        <v-spacer></v-spacer>
                        <span
                            icon="mdi-information"
                            v-tooltip.bottom="t('generic.info')"
                            class="ms-2"
                        >
                            <v-icon size="x-mall" color="grey-darken-1" style="opacity: 0.85">mdi-information</v-icon>
                        </span>
                    </v-card-title>
                <v-divider class="mb-2 mx-4"></v-divider>
                <v-list bg-color="background">
                    <v-row no-gutters>
                        <v-col v-for="(value, key) in improvements" :key="key" cols="12" md="6">
                            <v-list-item class="px-0">
                                <v-list-item-content class="d-flex align-center gap-2 flex-wrap ml-4"> <span
                                        class="font-weight-medium text-truncate"
                                        :class="{'text-muted': value === 0}"
                                    >
                                        {{ key }}
                                    </span>
                                    <v-chip
                                        :color="value > 0 ? 'primary' : 'grey'"
                                        label
                                        class="ml-4"
                                        :style="value > 0 ? { fontWeight: 'bold' } : { fontWeight: 'normal' }"
                                    >
                                        {{ value }}
                                    </v-chip>
                                </v-list-item-content>
                            </v-list-item>
                        </v-col>
                    </v-row>
                </v-list>
            </v-card>
        </v-container>

    </v-container>
</template>

<script>
import { fetch_campaigns, fetch_interview_runs, get_interview_run, delete_interview_run, get_interview_run_chart_data, build_domain_options, get_default_domain } from "../api.js";
import { VuePlotly } from 'vue3-plotly'
import RadarChart from "@/components/RadarChart.vue";
import { useI18n } from "vue-i18n";
import { en as vuetifyEn, ja as vuetifyJa } from 'vuetify/locale';
import { formatDateTime } from '@/utils/dateFormat';

export default {
    components: {
      RadarChart,
	  VuePlotly
	},
    setup() {
        const { t, locale } = useI18n();

        return {
        t,
        locale
        };
    },
    data() {
        return {
            // Vuetify's own locale message bundles, passed explicitly to the
            // page-scoped v-locale-provider below (the root Vuetify config
            // only registers "en", so "ja" must be supplied here for the
            // topic autocomplete's built-in empty-state text to localize).
            vuetifyEn,
            vuetifyJa,
            isLoading: false, // button loading
            campaigns: [], // List of campaigns fetched from the backend
            selectedCampaign: null, // Name of the currently selected campaign (for filtering)
            isCampaignsLoading: false, // Loading state

            // Interview list
            interviewRuns: [], // All interview runs for the selected domain
            isLoadingInterviews: false,
            selectedRunId: null, // Currently selected run ID
            selectedRunData: null, // Full run data fetched by ID
            isLoadingRunDetails: false,

            // Delete
            showDeleteConfirm: false,
            runToDelete: null,
            deletingRun: false,

            personasList: [], // Stores the list of personas
            improvements: {}, // The dict with all the suggestions from the users
            wordCloudUrl: null,

            // Toast notification
            showSnackbar: false,
            snackbarText: "",
            snackbarColor: "error",
            snackbarIcon: "mdi-alert-circle",

          
            selectedDomain: get_default_domain(),

            chartConfig: {
                displayModeBar: false, // Hide the modebar
                responsive: true,     // Make chart responsive to container size
            },

            bar_chart_visible: false,
            barChartData: [], // Holds the data for the chart
            

            sentimentChartData: [],

            // Donut chart data
            genderData: [],
            genderChartLayout:{},
            ageChartData: [],
            employmentChartData: [],
            incomeChartData: [],
            likelihoodChartData: [],
            likelihoodChartLayout: {
                //title: 'Likelihood to adhere',
            },
            absaChartData: [],
           radar_chart_model: [],
            // Raw payload from the last get_interview_run_chart_data call.
            // Kept so the Plotly traces (whose legend / label / annotation
            // text is i18n-resolved) can be rebuilt on locale change without
            // re-fetching or reloading the page.
            chartResponseData: null
        };
    },
    watch: {
      // Chart trace data embeds resolved i18n strings (legend names, pie
      // labels, the "participants" annotation). Rebuild the charts from the
      // cached payload when the app locale changes so English <-> Japanese
      // updates without a manual refresh.
      locale() {
        if (this.bar_chart_visible && this.chartResponseData) {
          this.generateDashboard(true);
        }
      },
      selectedDomain(newValue, oldValue){
        if(newValue !== oldValue){
          this.selectedCampaign = null;
          this.selectedRunId = null;
          this.selectedRunData = null;
          this.bar_chart_visible = false;
          this.fetchCampaigns(newValue);
          this.fetchInterviewRuns(newValue);
        }
      },
    },
    computed: {
        dropdownCampaigns() {
            return this.campaigns.map((campaign) => campaign.topic_name);
        },
        filteredRuns() {
            if (!this.selectedCampaign) return this.interviewRuns;
            return this.interviewRuns.filter(r => r.campaign_name === this.selectedCampaign);
        },
        domainOptions() {
            return build_domain_options(this.$t);
        },
        barChartLayout() {
            return {
            barmode: 'stack',
            dragmode: false,
            xaxis: {
                automargin: true,
            },
            yaxis: {
                title: this.$t('dashboard.values'),
            },
            margin: {
                b: 80
            }
            }
        },   
        sentimentChartLayout() {
            return {
            barmode: 'stack',
            dragmode: false,
            yaxis: {
                title: this.$t('dashboard.sentiment'),
            },
            }
        },
        ageChartLayout() {
            return {
            // title: this.$t('dashboard.ageDistribution'),
            barmode: 'stack',
            dragmode: false,
            xaxis: {
                title: this.$t('dashboard.ageGroups')
            },
            // yaxis: {
            //   title: this.$t('dashboard.participants')
            // }
            }
        },     
        employmentChartLayout() {
            return {
            // title: 'Employment Distribution by Likelihood',
            barmode: 'stack',
            dragmode: false,
            xaxis: {
                title: this.$t('dashboard.employmentStatus')
            },
            // yaxis: { title: 'Count' }
            }
        },
        incomeChartLayout() {
            return {
            // title: 'Income Distribution by Likelihood',
            barmode: 'stack',
            dragmode: false,
            xaxis: {
                title: this.$t('dashboard.incomeLevels')
            },
            // yaxis: { title: 'Count' }
            }
        },
        absaChartLayout() {
            return {
            title: this.$t('dashboard.aspectBasedSentimentAnalysisABSA'),
            barmode: 'stack',
            dragmode: false,
            // xaxis: { title: 'Count' },
            yaxis: {
                automargin: true
                // title: 'Aspect'
            }
            }
        }
    },
    methods: {
        // Show a toast notification with a message and optional type
        showToast(message, type = "error") {
            this.snackbarText = message;
            
            // Set appropriate color and icon based on notification type
            if (type === "error") {
            this.snackbarColor = "error"; // Reddish color for errors
            this.snackbarIcon = "mdi-alert-circle";
            } else if (type === "success") {
            this.snackbarColor = "success"; // Green color for success
            this.snackbarIcon = "mdi-check-circle";
            } else if (type === "warning") {
            this.snackbarColor = "warning"; // Amber/yellow for warnings
            this.snackbarIcon = "mdi-alert";
            } else if (type === "info") {
            this.snackbarColor = "info"; // Blue color for information
            this.snackbarIcon = "mdi-information";
            }
            
            this.showSnackbar = true;
        },

        // snake_case / kebab-case identifier -> "Title Case" (English fallback).
        humaniseKey(raw) {
            return String(raw)
                .replace(/[_-]+/g, ' ')
                .trim()
                .replace(/\b\w/g, c => c.toUpperCase());
        },

        // Map a chart-category key to a user-facing display label WITHOUT
        // changing the underlying identifier (kept for indexing the data).
        // Resolution order:
        //   1. bilingual label map from the backend (labelMap[key][locale]) —
        //      Key Points / ABSA categories, generated once per run;
        //   2. its English label if the current locale is missing;
        //   3. the fixed income-level i18n keys;
        //   4. the English humaniser fallback (also for legacy runs with no map).
        chartLabel(key, labelMap) {
            if (key === undefined || key === null) return key;
            const raw = String(key);
            const entry = labelMap && labelMap[raw];
            if (entry) {
                return entry[this.locale] || entry.en || this.humaniseKey(raw);
            }
            const incomeKey = {
                'low': 'dashboard.low2',
                'medium-low': 'dashboard.mediumLow',
                'medium': 'dashboard.medium',
                'medium-high': 'dashboard.mediumHigh',
                'high': 'dashboard.high2',
            }[raw.toLowerCase()];
            if (incomeKey) return this.$t(incomeKey);
            return this.humaniseKey(raw);
        },

        // Fetch the list of campaigns from the API
        async fetchCampaigns(selected_domain) {
            this.isCampaignsLoading = true;
            try {
                const response = await fetch_campaigns(selected_domain);
                if (response && Array.isArray(response.data)) {
                    this.campaigns = response.data;
                } else {
                    throw new Error("Invalid response format");
                }
            } catch (error) {
                console.error("Error fetching campaigns:", error);
                this.campaigns = [];
            } finally {
                this.isCampaignsLoading = false;
            }
        },

        // Fetch all interview runs for the selected domain
        async fetchInterviewRuns(selected_domain) {
            this.isLoadingInterviews = true;
            try {
                const response = await fetch_interview_runs(selected_domain);
                if (response && Array.isArray(response.data)) {
                    this.interviewRuns = response.data;
                } else {
                    this.interviewRuns = [];
                }
            } catch (error) {
                console.error("Error fetching interview runs:", error);
                this.interviewRuns = [];
            } finally {
                this.isLoadingInterviews = false;
            }
        },

        // Select a run and fetch its full details by run_id
        async selectRun(run) {
            this.selectedRunId = run.run_id;
            this.selectedRunData = null;
            this.bar_chart_visible = false;
            this.isLoadingRunDetails = true;
            try {
                const response = await get_interview_run(run.run_id);
                this.selectedRunData = run;
            } catch (error) {
                console.error("Error fetching run details:", error);
                this.showToast(this.$t('dashboard.failedToLoadRunDetails'), "error");
                this.selectedRunId = null;
            } finally {
                this.isLoadingRunDetails = false;
            }
        },

        formatTimestamp(ts) {
            return formatDateTime(ts, this.$i18n.locale);
        },

        // Delete run
        promptDeleteRun(run) {
            this.runToDelete = run;
            this.showDeleteConfirm = true;
        },

        async confirmDeleteRun() {
            if (!this.runToDelete) return;
            this.deletingRun = true;
            try {
                await delete_interview_run(this.runToDelete.run_id);
                this.interviewRuns = this.interviewRuns.filter(r => r.run_id !== this.runToDelete.run_id);
                if (this.selectedRunId === this.runToDelete.run_id) {
                    this.selectedRunId = null;
                    this.selectedRunData = null;
                    this.bar_chart_visible = false;
                }
                this.showToast(this.$t('dashboard.runDeletedSuccessfully'), "success");
            } catch (error) {
                console.error("Error deleting run:", error);
                this.showToast(this.$t('dashboard.failedToDeleteRun'), "error");
            } finally {
                this.deletingRun = false;
                this.showDeleteConfirm = false;
                this.runToDelete = null;
            }
        },

        // Method to call the API to generate the dashboard.
        // useCachedResponse=true reuses the payload from the previous call
        // (set by the `locale` watcher) so the i18n-resolved chart labels are
        // rebuilt on a language switch without re-fetching or reloading.
        async generateDashboard(useCachedResponse = false) {
            if (!this.selectedRunData) {
                this.showToast(this.$t('dashboard.pleaseSelectARunBeforeGeneratingTheDashboard'), "error");
                return;
            }
            this.isLoading = true;
            try {
                // Clear existing chart data before generating new chart
                this.barChartData = [];
                this.sentimentChartData = [];
                this.personasList = [];
                this.genderData = [];
                this.ageChartData = [];
                this.incomeChartData = [];
                this.employmentChartData = [];
                this.absaChartData = [];

                // Aggregated chart data for the selected run
                const response_data = (useCachedResponse && this.chartResponseData)
                    ? this.chartResponseData
                    : (await get_interview_run_chart_data(this.selectedRunData.run_id)).data;
                this.chartResponseData = response_data;

              // Radar Chart
              const radar_chart_data = response_data.radar_chart;
              if(radar_chart_data && radar_chart_data.averaged_scores){
                this.radar_chart_model = radar_chart_data.averaged_scores
                console.log("RADAR: ", this.radar_chart_model);
              }

                if (response_data && response_data.participants != 0) {
                    // Transform the API response into Plotly-friendly data
                    // this.barChartData = response_data.data.map((item) => ({
                    //     x: response_data.categories, // X-axis categories
                    //     y: item.values,        // Y-axis values
                    //     name: item.name,       // Segment name (e.g., High, Medium, Low, etc.)
                    //     type: 'bar',           // Bar chart type
                    // }));

                    this.wordCloudUrl = response_data.word_cloud_url;

                    console.log('response_data: ', response_data)

                    const keypointsCount = response_data.keypoints;
                    const keypointsCategories = Object.keys(keypointsCount);
                    // Display labels only — keypointsCategories keeps the raw
                    // (snake_case) keys used to index keypointsCount.
                    const keypointsLabels = keypointsCategories.map(c => this.chartLabel(c, response_data.keypoints_labels));
                    this.barChartData = [
                        {
                            x: keypointsLabels,
                            y: keypointsCategories.map(grade => keypointsCount[grade][0]), // "Likely"
                            name: this.$t('dashboard.likely'),
                            type: 'bar',
                        },
                        {
                            x: keypointsLabels,
                            y: keypointsCategories.map(grade => keypointsCount[grade][1]), // "Not Likely"
                            name: this.$t('dashboard.notLikely'),
                            type: 'bar',
                        },
                        {
                            x: keypointsLabels,
                            y: keypointsCategories.map(grade => keypointsCount[grade][2]), // "Unsure"
                            name: this.$t('dashboard.unsure'),
                            type: 'bar',
                        }
                    ];
                    
                    console.log('sentiment ', response_data.sentiment_dict)

                    // Populate sentiment chart data
                    if (response_data.sentiment_dict) {
                        const sentimentList = response_data.sentiment_dict;
                        this.sentimentChartData = [
                            {
                                x: [sentimentList.positive || 0], // Count for Positive sentiment
                                //y: ['Sentiment'],                // Y-axis label
                                name: this.$t('dashboard.positive'),               // Label for the bar segment
                                type: 'bar',
                                orientation: 'h',               // Horizontal bar
                                marker: { color: 'green' },     // Optional: Color for Positive
                            },
                            {
                                x: [sentimentList.neutral || 0], // Count for Neutral sentiment
                                //y: ['Sentiment'],
                                name: this.$t('dashboard.neutral'),
                                type: 'bar',
                                orientation: 'h',
                                marker: { color: '#87CEFA' },      // Optional: Color for Neutral, light blue
                            },
                            {
                                x: [sentimentList.negative || 0], // Count for Negative sentiment
                                //y: ['Sentiment'],
                                name: this.$t('dashboard.negative'),   
                                type: 'bar',
                                orientation: 'h',
                                marker: { color: 'red' },       // Optional: Color for Negative
                            },
                        ];
                    }

                    const likelihoodList = response_data.likelihood_dict;
                    // Likelihood to ahdere to plan, campaign or topic
                    this.likelihoodChartData = [{
                        values: [likelihoodList.likely, likelihoodList["not likely"], likelihoodList.unsure], // Example percentages         
                        labels: [
                            this.$t('dashboard.likely'),
                            this.$t('dashboard.notLikely'),
                            this.$t('dashboard.unsure')
                            ],
                        type: 'pie', // Pie chart type
                        textinfo: "label+percent",
                    }]

                    // Populate personas list
                    if (response_data.personas_list && Array.isArray(response_data.personas_list)) {
                        this.personasList = response_data.personas_list;
                    }
                    console.log('personas list to get data from: ', [...this.personasList])
                    // const persona_stats = await get_personas_chart_data([...this.personasList]);
                    // const persona_stats_data = persona_stats.data

                    // console.log('PERSONA STATS: ', persona_stats)

                    const genderDistribution = response_data.gender_distribution; // Example: {'low': [2,3,4], ...}


                    // this.participants = String(persona_stats_data.participants)
                    this.genderData = [{
                        values: [genderDistribution.male, genderDistribution.female], // Example percentages
                        labels: [this.$t('dashboard.male'), this.$t('dashboard.female'),],
                        type: 'pie', // Pie chart type
                        textinfo: "label+percent",
                        hole: 0.6
                    }]

                    this.genderChartLayout = {                
                        //title: 'Gender Distribution',
                        annotations: [
                            {
                            font: {
                                size: 28
                            },
                            showarrow: false,
                            text: response_data.participants,
                            x: 0.5,
                            y: 0.55
                            },
                            {
                            font: {
                                size: 20
                            },
                            showarrow: false,
                            text: this.$t('dashboard.participants'),
                            x: 0.5,
                            y: 0.45
                            }
                        ],
                    }

                    const incomeDistribution = response_data.income_distribution; // Example: {'low': [2,3,4], ...}
                    const incomeLabels = ['low', 'medium-low', 'medium', 'medium-high', 'high'].map(k => this.chartLabel(k));
                    this.incomeChartData = [
                        {
                            x: incomeLabels,
                            y: Object.values(incomeDistribution).map(val => val[0]), // "Likely"
                            name: this.$t('dashboard.likely'),
                            type: 'bar'
                        },
                        {
                            x: incomeLabels,
                            y: Object.values(incomeDistribution).map(val => val[1]), // "Not Likely"
                            name: this.$t('dashboard.notLikely'),
                            type: 'bar'
                        },
                        {
                            x: incomeLabels,
                            y: Object.values(incomeDistribution).map(val => val[2]), // "Unsure"
                            name: this.$t('dashboard.unsure'),
                            type: 'bar'
                        }
                    ];


                    const ageData = response_data.age_groups; // Example: {'group_1': [1,1,2], 'group_2': [3,5,0], ...}
                    // const jobCount = response_data.job_count; // Example: {'student': [2,4,3], 'freelancer': [1,2,2], ...}

                    this.ageChartData = [
                        {
                            x: ['0-18', '19-30', '31-45', '46-65', '66+'],
                            y: Object.values(ageData).map(val => val[0]), // "Likely"
                            name: this.$t('dashboard.likely'),
                            type: 'bar'
                        },
                        {
                            x: ['0-18', '19-30', '31-45', '46-65', '66+'],
                            y: Object.values(ageData).map(val => val[1]), // "Not Likely"
                            name: this.$t('dashboard.notLikely'),
                            type: 'bar'
                        },
                        {
                            x: ['0-18', '19-30', '31-45', '46-65', '66+'],
                            y: Object.values(ageData).map(val => val[2]), // "Unsure"
                            name: this.$t('dashboard.unsure'),
                            type: 'bar'
                        }
                    ];                

                    // const jobCount = persona_stats_data.job_count; // {student: 1, employed: 1, manager: 1, freelancer: 1, retired: 1}
                    
                    // Extracting the keys and values
                    // const jobCounts = Object.values(jobCount);    // [1, 1, 1, 1, 1]

                    // Generating the chart data
                    
                    const jobCount = response_data.job_count; // Example: {'student': [2,4,3], 'freelancer': [1,2,2], ...}
                    const jobCategories = Object.keys(jobCount);
                    const jobLabels = jobCategories.map(j => this.chartLabel(j));
                    this.employmentChartData = [
                        {
                            x: jobCategories.map(job => jobCount[job][0]), // "Likely"
                            y: jobLabels,
                            name: this.$t('dashboard.likely'),
                            type: 'bar',
                            orientation: 'h'
                        },
                        {
                            x: jobCategories.map(job => jobCount[job][1]), // "Not Likely"
                            y: jobLabels,
                            name: this.$t('dashboard.notLikely'),
                            type: 'bar',
                            orientation: 'h'
                        },
                        {
                            x: jobCategories.map(job => jobCount[job][2]), // "Unsure"
                            y: jobLabels,
                            name: this.$t('dashboard.unsure'),
                            type: 'bar',
                            orientation: 'h'
                        }
                    ];

                    // this.participants = String(persona_stats_data.participants)


                    const absaCount = response_data.absa;
                    const absaCategories = Object.keys(absaCount);
                    const absaLabels = absaCategories.map(a => this.chartLabel(a, response_data.absa_labels));
                    this.absaChartData = [
                        {
                            x: absaCategories.map(aspect => absaCount[aspect][0]), // "Likely"
                            y: absaLabels,
                            name: this.$t('dashboard.positive'),
                            type: 'bar',
                            orientation: 'h'
                        },
                        {
                            x: absaCategories.map(aspect => absaCount[aspect][1]), // "Not Likely"
                            y: absaLabels,
                            name: this.$t('dashboard.negative'),
                            type: 'bar',
                            orientation: 'h'
                        },
                        {
                            x: absaCategories.map(aspect => absaCount[aspect][2]), // "Unsure"
                            y: absaLabels,
                            name: this.$t('dashboard.neutral'),
                            type: 'bar',
                            orientation: 'h'
                        }
                    ];

                    // this.improvements = { ...response_data.improvements }; // Ensures Vue detects changes
                    this.improvements = Object.fromEntries(
                        Object.entries(response_data.improvements) // Convert the object into an array of key-value pairs
                            .sort(([, a], [, b]) => b - a) // Sort the array by value in descending order
                    );


                    this.bar_chart_visible = true;
                    this.isLoading = false;
                    console.log("Dashboard data generated successfully:", this.barChartData);
                } else {
                    this.showToast(this.$t('dashboard.failedToGenerateDashboardInvalidResponseFormat'), "error");
                }
            } catch (error) {
                console.error("Error generating dashboard:", error);
                this.showToast(this.$t('dashboard.anErrorOccurredWhileGeneratingTheDashboard'), "error");
            }
        },
    },
    async created() {
        try {
            await Promise.all([
                this.fetchCampaigns(this.selectedDomain),
                this.fetchInterviewRuns(this.selectedDomain),
            ]);
        } catch (error) {
            console.error("Error fetching initial data:", error);
            this.showToast(this.$t('dashboard.failedToLoadDataPleaseTryAgainLater'), "error");
        }
    },
};
</script>

<style scoped>
    /* This class preserves whitespace including newlines */
    .white-space-pre-wrap {
        white-space: pre-wrap;
    }

</style>